import once from 'lodash/once'

import { skipAbortError } from '@app/errors/AbortError'

import { noop } from '@app/utils/noop'
import { sleep } from '@app/utils/sleep'

import { dispose, withDisposableStack } from '@app/packages/disposable/disposable'
import { ComputedValue } from '@app/packages/reactives/ComputedValue'
import { ReactiveValue } from '@app/packages/reactives/ReactiveValue'

import './progress.scss'
import classes from './BlockingDialog.module.scss'

export interface ProgressInterface {
  wrap: <T>(p: Promise<T>, blocking?: boolean) => Promise<T>
  increment: (blocking?: boolean) => void
  decrement: (blocking?: boolean) => void
  /** Resolves once the blocking dialog is gone; until then the page takes no focus. */
  whenUnblocked: (signal?: AbortSignal) => Promise<void>
}

export class Progress implements ProgressInterface {
  private get ui() {
    const value = new ProgressUI(250, 250)

    Object.defineProperty(this, 'ui', {
      value,
      writable: false,
      configurable: false,
      enumerable: false,
    })

    return value
  }

  private get blockingUi() {
    const value = new ProgressUIBlocking(250, 250)

    Object.defineProperty(this, 'blockingUi', {
      value,
      writable: false,
      configurable: false,
      enumerable: false,
    })

    return value
  }

  increment(blocking = true) {
    if (blocking) this.blockingUi.start()
    this.ui.start()
  }

  decrement(blocking = true) {
    if (blocking) this.blockingUi.stop()
    this.ui.stop()
  }

  whenUnblocked(signal?: AbortSignal) {
    return this.blockingUi.whenUnblocked(signal)
  }

  async wrap<T>(promise: Promise<T>, blocking = true): Promise<T> {
    using stack = new DisposableStack()
    this.increment(blocking)
    stack.defer(() => {
      this.decrement(blocking)
    })
    return await promise
  }
}

class ProgressUI {
  private nativeui = new NativeProgressUI()
  private count = new ReactiveValue(0)
  private gracePeriod: number
  private endGracePeriod: number

  constructor(gracePeriod = 150, endGracePeriod = 300) {
    this.gracePeriod = gracePeriod
    this.endGracePeriod = endGracePeriod

    this.count.listen(c => {
      if (c === 1) {
        void Promise.all([import('nprogress'), sleep(this.gracePeriod)]).then(([m]) => {
          if (!this.active) return
          this.nativeui.start()
          m.default.start()
        })
      } else {
        void Promise.all([import('nprogress'), sleep(this.endGracePeriod)]).then(([m]) => {
          if (this.active) return
          this.nativeui.stop()
          m.default.done()
        })
      }
    })
  }

  start() {
    this.count.update(c => c + 1)
  }

  stop() {
    this.count.update(c => c - 1)
  }

  private get active() {
    return this.count.value > 0
  }
}

class NativeProgressUI {
  private promise: PromiseWithResolvers<void> | null = null

  private supported = once(() => {
    try {
      return 'navigation' in window && !!NavigateEvent.prototype.intercept
    } catch {
      return false
    }
  })

  start() {
    if (!this.supported()) return
    const promise = Promise.withResolvers<void>()
    this.promise = promise
    const nav = window.navigation
    const ac = new AbortController()
    nav.addEventListener(
      'navigate',
      event => {
        if (event.info === promise.promise) {
          event.intercept({
            scroll: 'manual',
            // This navigation exists to draw a progress bar, so it must not act like one:
            // `focusReset` defaults to `after-transition`, which hands focus back to the
            // document and takes it off whatever the user is typing in.
            focusReset: 'manual',
            handler: () => promise.promise,
          })
          event.stopPropagation()
        }
      },
      { once: true, signal: ac.signal }
    )

    try {
      const { committed, finished } = nav.navigate(location.href, { info: promise.promise, state: history.state })
      committed?.catch(noop)
      finished?.catch(noop)
    } catch {
      ac.abort()
      promise.resolve()
    }
  }

  stop() {
    this.promise?.resolve()
  }
}

export class ProgressUIBlocking {
  private count = new ReactiveValue(0)
  private shown = new ReactiveValue(false)
  private blocked = new ComputedValue([this.count, this.shown], (count, shown) => count > 0 || shown)
  private gracePeriod: number
  private endGracePeriod: number

  constructor(gracePeriod = 150, endGracePeriod = 300) {
    this.gracePeriod = gracePeriod
    this.endGracePeriod = endGracePeriod

    const active = new ComputedValue([this.count], c => c > 0)

    let dialogDisposable: Disposable | null = null
    let taskController: AbortController | null = null

    active.sync(isActive => {
      if (taskController !== null) {
        taskController.abort()
        taskController = null
      }
      if (isActive) {
        if (dialogDisposable !== null) return
        const ctrl = new AbortController()
        taskController = ctrl
        sleep(this.gracePeriod, ctrl.signal)
          .then(() => {
            taskController = null
            dialogDisposable = this.createDialog()
          })
          .catch(skipAbortError)
      } else {
        if (dialogDisposable === null) return
        const ctrl = new AbortController()
        taskController = ctrl
        sleep(this.endGracePeriod, ctrl.signal)
          .then(() => {
            taskController = null
            if (dialogDisposable !== null) {
              dispose(dialogDisposable)
              dialogDisposable = null
            }
          })
          .catch(skipAbortError)
      }
    })
  }

  start() {
    this.count.update(c => c + 1)
  }

  stop() {
    this.count.update(c => c - 1)
  }

  whenUnblocked(signal?: AbortSignal) {
    return new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(signal.reason)
        return
      }
      if (!this.blocked.value) {
        resolve()
        return
      }
      const stack = new DisposableStack()
      stack.use(
        this.blocked.listen(blocked => {
          if (blocked) return
          stack.dispose()
          resolve()
        })
      )
      if (signal) {
        const handleAbort = () => {
          stack.dispose()
          reject(signal.reason)
        }
        signal.addEventListener('abort', handleAbort)
        stack.defer(() => signal.removeEventListener('abort', handleAbort))
      }
    })
  }

  private createDialog() {
    return withDisposableStack(stack => {
      const dialog = document.createElement('dialog')
      const contentTemplate = document.createElement('template')
      contentTemplate.innerHTML = `
        <div class="${classes.content}">
          <div class="${classes.spinner}"></div>
        </div>
      `.trim()
      dialog.appendChild(contentTemplate.content)
      document.body.appendChild(dialog)
      stack.defer(() => document.body.removeChild(dialog))
      dialog.className = classes.root
      dialog.showModal()
      stack.defer(() => dialog.close())
      this.shown.update(() => true)
      stack.defer(() => this.shown.update(() => false))
    })
  }
}
